import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Hls from "hls.js";
import api from "@/lib/api";
import { Radio as RadioIcon, Tv, Eye, ChevronRight, Volume2, VolumeX } from "lucide-react";

const ROTATE_MS = 7000;

function TvPreview({ media, muted }) {
  const ref = useRef(null);
  const [ok, setOk] = useState(true);
  useEffect(() => {
    const video = ref.current;
    if (!video || !media.hls_url || !media.hls_url.toLowerCase().includes(".m3u8")) { setOk(false); return; }
    let hls;
    setOk(true);
    if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(media.hls_url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, d) => { if (d.fatal) setOk(false); });
      video.play?.().catch(() => {});
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = media.hls_url;
      video.play?.().catch(() => {});
    } else { setOk(false); }
    return () => { if (hls) hls.destroy(); if (video) { video.pause(); video.removeAttribute("src"); } };
  }, [media.hls_url]);

  useEffect(() => { if (ref.current) { ref.current.muted = muted; if (!muted) ref.current.play?.().catch(() => {}); } }, [muted]);

  if (!ok) return <img src={media.banner} alt="" className="w-full h-full object-cover" />;
  return <video ref={ref} muted autoPlay playsInline poster={media.banner} className="w-full h-full object-cover" />;
}

export default function LiveCarousel() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => { api.get("/live-carousel?count=10").then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [items]);

  const m = items[i];

  // Radio audio preview (only after user enables sound — browser gesture requirement)
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (soundOn && m && m.type === "radio" && m.audio_url) {
      if (a.src !== m.audio_url) a.src = m.audio_url;
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, [soundOn, m]);

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  if (items.length === 0) return null;
  const isTv = m.type === "tv";

  const toggleSound = (e) => { e.preventDefault(); e.stopPropagation(); setSoundOn((s) => !s); };

  return (
    <section className="mt-6" data-testid="live-carousel">
      <Link to={`/${m.type}/${m.slug}`} data-testid="live-carousel-feature" className="group relative block aspect-[16/7] sm:aspect-[16/6] rounded-3xl overflow-hidden border border-border bg-black">
        <div className="absolute inset-0">
          {isTv ? <TvPreview key={m.id} media={m} muted={!soundOn} /> : <img src={m.banner} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        {!isTv && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 text-rdcgold">
            {[0, 1, 2, 3, 4].map((k) => <span key={k} className="eq-bar w-1.5" style={soundOn ? { animationDelay: `${k * 0.13}s` } : { height: "24%", animation: "none" }} />)}
          </div>
        )}

        <button onClick={toggleSound} data-testid="live-sound-toggle" title={soundOn ? "Couper le son" : "Activer l'aperçu audio"}
          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white z-10">
          {soundOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex items-end gap-3">
          <img src={m.logo} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 border-white/80 shadow-xl bg-white shrink-0" />
          <div className="min-w-0 pb-1">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase ${isTv ? "bg-rdcblue text-white" : "bg-rdcgold text-amber-950"}`}>{isTv ? <Tv size={11} /> : <RadioIcon size={11} />}{isTv ? "TV" : "Radio"}</span>
              {m.live_viewers > 0 && <span className="flex items-center gap-1 rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] text-white font-mono"><Eye size={11} />{m.live_viewers.toLocaleString()}</span>}
              {soundOn && m.type === "radio" && <span className="rounded-md bg-rdcgold/90 text-amber-950 px-1.5 py-0.5 text-[10px] font-bold uppercase">Aperçu audio</span>}
            </div>
            <h3 className="font-heading font-extrabold text-white text-xl sm:text-2xl truncate">{m.name}</h3>
            <p className="text-white/80 text-xs sm:text-sm truncate">{m.province} · {(m.categories || [])[0]}</p>
          </div>
          <span className="ml-auto hidden sm:flex items-center gap-1 rounded-full bg-white/15 group-hover:bg-white/25 px-4 py-2 text-white text-sm font-semibold transition-colors shrink-0">
            {isTv ? "Regarder" : "Écouter"} <ChevronRight size={16} />
          </span>
        </div>
      </Link>

      {/* Thumbnails / progress */}
      <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
        {items.map((it, idx) => (
          <button key={it.id} onClick={() => setI(idx)} onMouseEnter={() => setI(idx)} data-testid={`live-thumb-${it.slug}`}
            className={`relative shrink-0 w-24 sm:w-28 aspect-video rounded-xl overflow-hidden border-2 transition-all ${idx === i ? "border-rdcred" : "border-transparent opacity-60 hover:opacity-100"}`}>
            <img src={it.banner || it.logo} alt="" className="w-full h-full object-cover" />
            {idx === i && <span className="absolute bottom-0 left-0 h-1 bg-rdcred animate-[grow_7s_linear]" style={{ width: "100%" }} />}
            <span className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-rdcred live-dot" />
          </button>
        ))}
      </div>

      <audio ref={audioRef} preload="none" />
    </section>
  );
}

